import React from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Button,
  useColorModeValue,
  useBreakpointValue,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  HStack,
  Badge,
} from '@chakra-ui/react';
import {
  HamburgerIcon,
  BellIcon,
} from '@chakra-ui/icons';
import { FiUser, FiSettings, FiLogOut, FiHome } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import config from '../../config/config';

/**
 * AdminHeader component for the admin dashboard
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onOpen - Function to open the mobile sidebar
 * @returns {React.ReactNode} - Rendered component
 */
const AdminHeader = ({ onOpen }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Handle user logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      bg={useColorModeValue('white', 'gray.800')}
      borderBottom="1px"
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      px={4}
      w="full"
      zIndex="1000"
      h="64px"
      flexShrink={0}
    >
      <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
        {/* Mobile menu button */}
        <IconButton
          variant="ghost"
          onClick={onOpen}
          aria-label="فتح القائمة"
          display={{ md: 'none' }}
          icon={<HamburgerIcon />}
        />

        {/* Logo */}
        <Text
          textAlign={useBreakpointValue({ base: 'center', md: 'right' })}
          fontFamily={'heading'}
          color={useColorModeValue('gray.800', 'white')}
          fontWeight="bold"
          fontSize="xl"
        >
          {config.app.name} | لوحة التحكم
        </Text>

        {/* Right side items */}
        <HStack spacing={3}>
          {/* Home button */}
          <Box
            as="a"
            href="/"
            target="_self"
            onClick={(e) => {
              // Only handle left clicks - middle clicks will use default browser behavior
              if (e.button === 0) {
                e.preventDefault();
                navigate('/');
              }
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            w="40px"
            h="40px"
            color="gray.600"
            _hover={{ bg: 'gray.100', color: 'teal.500' }}
            title="الصفحة الرئيسية"
            aria-label="الصفحة الرئيسية"
          >
            <FiHome size="20px" />
          </Box>
          
          {/* Notifications */}
          <Menu>
            <MenuButton
              as={IconButton}
              size="lg"
              variant="ghost"
              aria-label="الإشعارات"
              icon={
                <>
                  <BellIcon w={5} h={5} />
                  <Badge
                    colorScheme="red"
                    position="absolute"
                    top="1"
                    left="1"
                    borderRadius="full"
                    w={4}
                    h={4}
                    fontSize="xs"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    3
                  </Badge>
                </>
              }
            />
            <MenuList zIndex={1001}>
              <MenuItem>إشعار جديد: تم تقديم طلب ترشيح جديد</MenuItem>
              <MenuItem>إشعار جديد: تم تقييم المرشح أحمد محمد</MenuItem>
              <MenuItem>إشعار جديد: تم تعيين مستخدم جديد</MenuItem>
              <MenuDivider />
              <MenuItem fontWeight="bold">عرض جميع الإشعارات</MenuItem>
            </MenuList>
          </Menu>

          {/* User menu */}
          <Menu>
            <MenuButton
              as={Button}
              rounded={'full'}
              variant={'link'}
              cursor={'pointer'}
              minW={0}
            >
              <Avatar
                size={'sm'}
                name={currentUser?.name || 'مستخدم النظام'}
                src={currentUser?.avatar || ''}
              />
            </MenuButton>
            <MenuList zIndex={1001}>
              <MenuItem icon={<FiUser />}>الملف الشخصي</MenuItem>
              <MenuItem icon={<FiSettings />}>الإعدادات</MenuItem>
              <MenuDivider />
              <MenuItem icon={<FiLogOut />} onClick={handleLogout}>
                تسجيل الخروج
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
};

export default AdminHeader;
